import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { CalendarTemplate } from './index'
import { useBFSMValue } from '../../services/hooks/useBFSMValues'
import {
    BasisActions,
    BasisState,
    CalendarActions,
    CalendarState,
} from '@raskrask-dk/bookingflow-state-machine'
import { Period, RRDate } from '@raskrask-dk/raskrask-utils'
import { useBFSM } from '../../contexts/bfsmContext'
import { useLoadCalendar } from './useLoadCalendar'
import { getCaruselDates } from './connectModels'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import {
    getFlow,
    getIsLoading,
    getPreferedPartnerId,
    setFlow,
    setPreferedParnterId,
} from '../../redux/reducers/appSettings'
import Slider from 'react-slick'
import { InitialLoading } from '../chemistry/calendarComponents/loading'
import { Snackbar, useLockedBody } from '@raskrask-dk/rr-components'
import { StyledCalender } from './styles'
import { t } from 'i18next'
import {
    getActiveTab,
    setActiveTab,
} from '../../redux/reducers/calendarSettings'
import { TimeSlot } from '@raskrask-dk/rr-components/lib/Organisms/ProductModal/Tabs/Book/props'
import { MapTimeSlot } from '../chemistry/calendarComponents/partnerModal/partnermodal'
import { sortGoals } from './sortGoals'
import { BasisTypeBF, GoalBF, PartnerBF, ServiceBF } from '../../models'

export const CalendarPage = (): ReactElement => {
    const [BFSM] = useBFSM()
    const dispatch = useAppDispatch()

    const isLoading = useAppSelector(getIsLoading)
    const flow = useAppSelector(getFlow)
    const preferedPartnerId = useAppSelector(getPreferedPartnerId)
    const activeTab = useAppSelector(getActiveTab)

    const [basisType] = useBFSMValue<
        BasisState,
        BasisActions.SetBasisType,
        'basisType'
    >('basisType')
    const [goal, setGoal] = useBFSMValue<
        CalendarState,
        CalendarActions.SetGoal,
        'goal'
    >('goal', CalendarActions.SetGoal)
    const [service, setService] = useBFSMValue<
        CalendarState,
        CalendarActions.SetServiceType,
        'serviceType'
    >('serviceType', CalendarActions.SetServiceType)
    const [partner, setPartner] = useBFSMValue<
        CalendarState,
        CalendarActions.SetPartner,
        'partner'
    >('partner', CalendarActions.SetPartner)
    const [date, setDate] = useBFSMValue<
        CalendarState,
        CalendarActions.SetDate,
        'date'
    >('date', CalendarActions.SetDate)
    const [time, setTime] = useBFSMValue<
        CalendarState,
        CalendarActions.SetTime,
        'time'
    >('time', CalendarActions.SetTime)

    const isCompanyBooking =
        BFSM.getData<BasisState>().customerType === 'Company'
    const [numberOfDaysLoaded, setNumberOfDaysLoaded] = useState(27)
    const slideLength = 14
    const [shouldCarouselAutoScroll, setShouldCarouselAutoScroll] =
        useState(true)
    const carouselRef = useRef<Slider>(null)
    const partnerModalCarouselRef = useRef<Slider>(null)
    const isModalOpen = partner !== undefined
    const loadingStartDate = useMemo(() => new RRDate(), [])
    const [, setLocked] = useLockedBody()
    const onClickBook = () => {
        if (activeTab === 'book') {
            BFSM.doAction(CalendarActions.Next, undefined)
        } else {
            dispatch(setActiveTab('book'))
        }
    }
    const canClicknext = BFSM.canDo(CalendarActions.Next)
    const [goals, setGoals] = useState<GoalBF[]>([])
    const [rebookPartnerNotAvailable, setRebookPartnerNotAvailable] =
        useState(false)

    const services: ServiceBF[] = goal
        ? ((goal as GoalBF).services as ServiceBF[])
        : []

    const partners: PartnerBF[] =
        service && goal
            ? (goal.services
                  .find((s) => s.id === service.id)
                  ?.getPartnersBy(date) as PartnerBF[]) ?? []
            : []

    useLoadCalendar(
        setGoals,
        setGoal,
        numberOfDaysLoaded,
        loadingStartDate,
        flow,
        partners.map((p) => p?.id),
        preferedPartnerId
    )
    const carouselDates = useMemo(
        () =>
            getCaruselDates(
                numberOfDaysLoaded,
                loadingStartDate,
                service as ServiceBF,
                date,
                partner as PartnerBF
            ),
        [numberOfDaysLoaded, loadingStartDate, service, date, partner]
    )

    const timeSlots = (): TimeSlot[] => {
        if (!partner || !date || !service) return []
        const tt = partner.schedule.timeslotsByDay(date, service.duration)
        const ll = tt.map((timeslot: Period) => {
            const timeslotString = timeslot.from.toString('HH:mm')
            return MapTimeSlot(
                {
                    start: timeslotString,
                    end: timeslotString,
                },
                timeslotString === time
            )
        })
        return ll
    }

    const onScrollEvent = (_firstShown: string, lastShown: string) => {
        const lastDateShown = new RRDate(lastShown, 'yyyy-MM-dd')
        const daysInArray = carouselDates.length
        const indexOfCurrentLastDate = carouselDates
            .map((d) => d.date)
            .indexOf(lastDateShown.toString())
        const shallLoadMore = daysInArray - 1 == indexOfCurrentLastDate
        if (shallLoadMore) loadMoreDays(slideLength)
    }

    const loadMoreDays = async (dayNumber: number) => {
        const models = await BFSM.service.loadMore(dayNumber, basisType!)
        const sorted = sortGoals(models.goals as GoalBF[])
        setGoals(sorted) // TODO: this could AND SHOULD die
        const forcedGoal =
            goal === undefined
                ? undefined
                : sorted.find((g) => g.id === goal.id)
        setGoal(forcedGoal) // TODO: this could AND SHOULD die
        setNumberOfDaysLoaded(numberOfDaysLoaded + dayNumber) // TODO: this could AND SHOULD die
    }

    const handleOnOpenModal = () => {
        setLocked(true)

        if (carouselDates && date && shouldCarouselAutoScroll) {
            const index = carouselDates.findIndex(
                (thisDate) => thisDate.date === date.toString('yyyy-MM-dd')
            )

            partnerModalCarouselRef.current?.slickGoTo(index, true)
            if (partnerModalCarouselRef.current !== null)
                setShouldCarouselAutoScroll(false)
        }
    }

    const handleOnCloseModal = () => {
        setShouldCarouselAutoScroll(true)
        setPartner(undefined)
        setDate(undefined)
        setLocked(false)
        dispatch(setPreferedParnterId(undefined))
        carouselRef.current?.slickGoTo(0, true)
    }

    const onClickDate = (thisDate: string) => {
        !date?.equals(new RRDate(thisDate, 'yyyy-MM-dd'))
            ? setDate(new RRDate(thisDate, 'yyyy-MM-dd'))
            : setDate(undefined)
    }

    const onClickKlippeKortButtonHeaderDesktop = (): void => {
        BFSM.doAction(CalendarActions.ChoosePackage, undefined)
    }

    // The onClickPartner has been wrapped in this func to make the action of unselecting the partner in the rebooking flow trigger a new schedules call
    const onClickPartner = (partner: PartnerBF | undefined) => {
        if (!partner && flow === 'rebook') {
            setFlow('Booking')
        }
        setPartner(partner)
    }

    useEffect(() => {
        if (date && partner && service && !time) {
            const ts = partner.schedule.timeslotsByDay(
                date,
                service.duration
            )[0]
            setTime(ts.from.toString('HH:mm'))
        }
    }, [date, partner, service, setTime, time])

    useEffect(() => {
        if (!date && partner && service) {
            const firstDate = partner.schedule
                .datesAvilabilities(service.duration)
                .sort((a, b) => a.toMillis() - b.toMillis())
            if (firstDate.length > 0) {
                setDate(
                    new RRDate(
                        firstDate[0].toString('yyyy-MM-dd'),
                        'yyyy-MM-dd'
                    )
                )
            } else {
                setPartner(undefined)
            }
        }
    }, [date, partner, service, setDate])

    useEffect(() => {
        if (flow === 'rebook' && preferedPartnerId !== undefined && service) {
            // If flow === 'rebook' && no partner is selected, the given partner isn't available and we must show an 'error'/information message
            const rebookingPartner = partners.find(
                (p) => p.id === preferedPartnerId
            )
            if (rebookingPartner) {
                const firstDate = rebookingPartner.schedule
                    .datesAvilabilities(service.duration)
                    .sort((a, b) => a.toMillis() - b.toMillis())
                setRebookPartnerNotAvailable(firstDate.length === 0)
            } else {
                setRebookPartnerNotAvailable(true)
            }
        } else setRebookPartnerNotAvailable(false)
    }, [flow, partners, preferedPartnerId, partner, service])

    if (isLoading)
        return (
            <InitialLoading
                basisType={basisType as BasisTypeBF}
                elementsToShow={{
                    showCarouselLoading: true,
                    showGoalsLoading: true,
                    showPartnerCardsLoading: true,
                    showServicePickerLoading: true,
                    showServiceTypesLoading: true,
                }}
            />
        )

    return (
        <StyledCalender>
            {rebookPartnerNotAvailable && (
                <Snackbar action={'information'} isOpen={true}>
                    {t('calendar.rebook.oldpartnernotavailable')}
                </Snackbar>
            )}
            <CalendarTemplate
                basisType={basisType as BasisTypeBF}
                goalHandler={[goal as GoalBF, setGoal, goals]}
                serviceHandler={[service as ServiceBF, setService, services]}
                partnerHandler={[
                    partner as PartnerBF,
                    onClickPartner,
                    partners,
                ]}
                dateHandler={[
                    date as RRDate,
                    (date: string | undefined) =>
                        setDate(new RRDate(date, 'yyyy-MM-dd')),
                    carouselDates,
                    onScrollEvent,
                    carouselRef,
                    partnerModalCarouselRef,
                    onClickDate,
                ]}
                modalHandler={[
                    handleOnCloseModal,
                    handleOnOpenModal,
                    isModalOpen,
                ]}
                timeHandler={[time as string, timeSlots(), setTime]}
                onClickBook={onClickBook}
                canClickNext={canClicknext}
                onClickKlippeKortButtonHeaderDesktop={
                    onClickKlippeKortButtonHeaderDesktop
                }
                isCompanyBooking={isCompanyBooking}
            />
        </StyledCalender>
    )
}
